import React, { useEffect, useState } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button,
  Modal,
  TextInput,
  TableToolbarAction,
  TableToolbarMenu,
  Pagination,
} from "@carbon/react";
import { Add } from "@carbon/icons-react";
import { callApi } from "../../../services/apiService";

const headers = [
  { key: "attributeSpecs", header: "Attribute Specs" },
  { key: "status", header: "Status" },
  { key: "createdBy", header: "Created By" },
  { key: "createdTime", header: "Created Time" },
  { key: "actions", header: "Actions" },
];

const AttributeSpecs = () => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [specName, setSpecName] = useState("");
  const [status, setStatus] = useState("Active");
  const [selectedRow, setSelectedRow] = useState(null);

  // Validation state
  const [nameError, setNameError] = useState("");

  // Delete modal state
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Pagination and Search states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const userId = 1; // 🔹 replace with logged-in user ID from context/auth

  // Debounced search effect
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Filter rows based on search term
  useEffect(() => {
    if (!debouncedSearch) {
      setFilteredRows(rows);
    } else {
      const filtered = rows.filter(row =>
        row.attributeSpecs?.toLowerCase().includes(debouncedSearch) ||
        row.status?.toLowerCase().includes(debouncedSearch) ||
        row.createdBy?.toLowerCase().includes(debouncedSearch) ||
        row.createdTime?.toLowerCase().includes(debouncedSearch)
      );
      setFilteredRows(filtered);
    }
    setPage(1); 
  }, [debouncedSearch, rows]);

  const paginatedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  // Fetch data
  useEffect(() => {
    fetchSpecs();
  }, []);

  const fetchSpecs = async () => {
    try {
      const response = await callApi("GET", "/attribute_specs/");
      const formattedRows = response.data.map((item) => ({
        id: item.id,
        attributeSpecs: item.atr_spec_name,
        status: item.status,
        createdBy: item.created_user,
        createdTime: item.created_at,
        
      }));
      setRows(formattedRows);
    } catch (error) {
      console.error("Failed to fetch specs:", error.message);
    }
  };

  // Open modal for Add/Edit - FIXED: Get original row data from rows array
  const openModal = (rowId = null) => {
    setNameError(""); // Reset validation error
    if (rowId) {
      // Find the original row data from the rows array using the ID
      const originalRow = rows.find(row => row.id === rowId);
      if (originalRow) {
        setSpecName(originalRow.attributeSpecs);
        setStatus(originalRow.status);
        setSelectedRow(originalRow);
      }
    } else {
      setSpecName("");
      setStatus("Active");
      setSelectedRow(null);
    }
    setIsModalOpen(true);
  };

  // Reset modal
  const resetModal = () => {
    setIsModalOpen(false);
    setSpecName("");
    setStatus("Active");
    setSelectedRow(null);
    setNameError("");
  };

  // Validate form
  const validateForm = () => {
    let isValid = true;

    if (!specName.trim()) {
      setNameError("Attribute Spec name is required");
      isValid = false;
    } else {
      setNameError("");
    }

    return isValid;
  };

  // Save (Add / Update)
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (selectedRow) {
        // Update existing
        const payload = {
          ATTRIBUTE_SPEC_ID: selectedRow.id,
          ATTRIBUTE_SPEC_NAME: specName,
          STATUS: status,
          UPDATED_BY_USER_ID: userId,
        };
        await callApi("PUT", "/attribute_specs/", payload);
      } else {
        // Create new
        const payload = {
          ATTRIBUTE_SPEC_NAME: specName,
          STATUS: status,
          CREATED_BY_USER_ID: userId,
        };
        await callApi("POST", "/attribute_specs/", payload);
      }

      // ✅ Refresh list after save
      await fetchSpecs();
      resetModal();
    } catch (error) {
      console.error("Failed to save spec:", error.message);
    }
  };

  // Ask before delete
  const confirmDelete = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  // Actually delete
  const handleConfirmDelete = async () => {
    try {
      await callApi("DELETE", `/attribute_specs/${deleteId}`);
      // Refresh list after delete
      await fetchSpecs();
    } catch (error) {
      console.error("Failed to delete spec:", error.message);
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteId(null);
    }
  };

  return (
    <div>
      <DataTable rows={paginatedRows} headers={headers}>
        {({ rows, headers, getHeaderProps, getRowProps }) => (
          <TableContainer title="Attribute Specs">
            <TableToolbar>
              <TableToolbarContent>
                <TableToolbarSearch 
                  persistent 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search attribute specs..."
                />
                <Button renderIcon={Add} onClick={() => openModal()}>
                  Add New
                </Button>
              </TableToolbarContent>
            </TableToolbar>

            <Table>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableHeader key={header.key} {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} {...getRowProps({ row })}>
                    <TableCell>{row.cells[0].value}</TableCell>
                    <TableCell>{row.cells[1].value}</TableCell>
                    <TableCell>{row.cells[2].value}</TableCell>
                    <TableCell>{row.cells[3].value}</TableCell>
                    <TableCell>
                      <TableToolbarMenu>
                        {/* FIXED: Pass the row ID instead of the row object */}
                        <TableToolbarAction onClick={() => openModal(row.id)}>
                          Edit
                        </TableToolbarAction>
                        <TableToolbarAction onClick={() => confirmDelete(row.id)}>
                          Delete
                        </TableToolbarAction>
                      </TableToolbarMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>

      {/* Pagination */}
      {filteredRows.length > 0 && (
        <div style={{ padding: "1rem 1.25rem", display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            totalItems={filteredRows.length}
            page={page}
            pageSize={pageSize}
            pageSizes={[5, 10, 20, 50]}
            onChange={({ page: p, pageSize: ps }) => { 
              setPage(p); 
              setPageSize(ps); 
            }}
          />
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={isModalOpen}
        onRequestClose={resetModal}
        modalHeading={selectedRow ? "Edit Spec" : "Add New Spec"}
        primaryButtonText={selectedRow ? "Update" : "Save"}
        secondaryButtonText="Cancel"
        onRequestSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <TextInput
          id="spec-name"
          labelText="Attribute Spec Name"
          value={specName}
          onChange={(e) => setSpecName(e.target.value)}
          placeholder="Enter attribute spec name"
          invalid={!!nameError}
          invalidText={nameError}
          required
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        danger
        onRequestClose={() => setIsDeleteModalOpen(false)}
        onRequestSubmit={handleConfirmDelete}
      >
        <p>Are you sure you want to delete this record?</p>
      </Modal>
    </div>
  );
};

export default AttributeSpecs;